import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, XCircle, UserCircle, ShieldPlus } from "lucide-react";

// Component Imports
import MainLayout from "../../../layout";
import FormTextField from "../../../components/form/text";
import { FormImageField } from "../../../components/form/image";
import { FormTextAreaField } from "../../../components/form/text-area";
import { url } from "../../../config";
import successToast from "../../../components/toast/successToast";
import errorToast from "../../../components/toast/errorToast";

const CreateUsers = () => {
  const navigate = useNavigate();

  const initialValues = {
    profile_picture: "",
    email: "",
    name: "",
    password: "",
    description: "",
    slug: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Full name is required"),
    email: Yup.string()
      .required("Email is required")
      .email("Invalid email format"),
    password: Yup.string()
      .min(6, "Password too short")
      .required("Password is required"),
  });

  const handleSubmit = (values) => {
    axios
      .post(`${url}/ffadmin/create`, values)
      .then((response) => {
        successToast("User created successfully");
        navigate(`/admin/users/list`);
      })
      .catch((error) => {
        errorToast(error?.response?.data?.message || "Failed to create user");
      });
  };

  return (
    <MainLayout>
      <div className="flex flex-col h-full bg-[#F8FAFC]">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col h-full">
              {/* --- Sticky Header Action Bar --- */}
              <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() => navigate("/admin/users/list")}
                    className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div>
                    <h1 className="text-xl font-bold text-slate-800">
                      Create Admin User
                    </h1>
                    <p className="text-xs text-slate-500 font-medium tracking-wide">
                      Configure new system access
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate("/admin/users/list")}
                    type="button"
                    className="hidden sm:flex items-center gap-2 px-4 py-2 text-slate-600 font-bold text-sm hover:text-red-600 transition-colors"
                  >
                    <XCircle size={18} /> Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Save size={18} />{" "}
                    {isSubmitting ? "Creating..." : "Save User"}
                  </button>
                </div>
              </header>

              {/* --- Form Body --- */}
              <div className="flex-1 overflow-auto p-4 sm:p-4 custom-scrollbar">
                <div className="max-w-6xl mx-auto">
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-6">
                    <div className="space-y-4 w-full max-w-[550px]">
                      <div className="mb-4">
                        <FormImageField
                          name={"profile_picture"}
                          label={"Profile Image"}
                        />
                      </div>
                      <div className="my-4">
                        <FormTextField
                          name={"name"}
                          label={"Full Name"}
                          placeholder={`Enter user's full name`}
                        />
                      </div>

                      <FormTextField
                        name={"email"}
                        label={"Email Address"}
                        placeholder={`example@pos.com`}
                      />

                      <FormTextField
                        name={"password"}
                        label={"Security Password"}
                        placeholder={`Min. 6 characters`}
                        type="password" // Added security type
                      />

                      <div className="md:col-span-2">
                        <FormTextAreaField
                          name={"description"}
                          label={"Biography / Internal Notes"}
                          placeholder={`Brief details about the user's role or department...`}
                          rows={4}
                        />
                      </div>

                       <FormTextField
                        name={"slug"}
                        label={"Slug"}
                        placeholder={`mohit-mittal`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </MainLayout>
  );
};

export default CreateUsers;
